import ErrorIcon from "@mui/icons-material/HighlightOff";
import { Product } from "models/Product";
import {
  IconButton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import StyledTable from "components/StyledTable";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import TableRowAction from "components/TableRowAction";
import EditOutlined from "@mui/icons-material/EditOutlined";
import PlaylistAdd from "@mui/icons-material/PlaylistAdd";
import Check from "@mui/icons-material/Check";
import { useSortedData } from "lib/hooks/useSortedData";
import { useMemo, useState } from "react";
import TableRowMenu from "components/TableRowMenu";
import { DeleteOutline } from "@mui/icons-material";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";

type ProductAction = "edit" | "add-child" | "delete" | "move" | "uninstall";
export type ActionHandler = (serial: Product, action: ProductAction) => void;

type Props = {
  serials: Product[];
  onAction?: ActionHandler;
};

const OrderSerialsTable: React.FC<Props> = ({ serials, onAction }) => {
  const { hasRole } = useAuthContext();
  const canEdit = hasRole([
    UserRole.ADMIN,
    UserRole.USER_ADMIN,
    UserRole.SALES,
    UserRole.INTERNAL_INSTALLER,
    UserRole.EXTERNAL_INSTALLER,
  ]);
  const [isSerialsExpanded, setIsSerialsExpanded] = useState<Map<string, boolean>>(new Map());

  const {
    data: sortedSerials,
    sortBy,
    sortDir,
    setSortBy,
  } = useSortedData(serials, [
    { key: "item" },
    { key: "description" },
    { key: "serial", defaultSortDir: "desc" },
    { key: "existing" },
  ]);

  const expandedSerials = useMemo(() => {
    return sortedSerials.flatMap((s) =>
      isSerialsExpanded.get(s.id) ? [s, ...s.children] : [s]
    ) as Product[];
  }, [isSerialsExpanded, sortedSerials]);

  const handleToggleExpand = (id: string) => {
    setIsSerialsExpanded((prev) => {
      const newMap = new Map(prev);
      newMap.set(id, !newMap.get(id));
      return newMap;
    });
  };

  return (
    <TableContainer>
      <StyledTable size="small" striped noBottomBorder>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "item"}
                direction={sortDir}
                onClick={() => setSortBy("item")}
              >
                Artikelkod
              </TableSortLabel>
            </TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "description"}
                direction={sortDir}
                onClick={() => setSortBy("description")}
              >
                Benämning
              </TableSortLabel>
            </TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "serial"}
                direction={sortDir}
                onClick={() => setSortBy("serial")}
              >
                Serienummer
              </TableSortLabel>
            </TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "room"}
                direction={sortDir}
                onClick={() => setSortBy("room")}
              >
                Rum
              </TableSortLabel>
            </TableCell>

            <TableCell align="right">
              <TableSortLabel
                active={sortBy === "existing"}
                direction={sortDir}
                onClick={() => setSortBy("existing")}
              >
                Befintlig
              </TableSortLabel>
            </TableCell>

            <TableCell align="right">
              <TableSortLabel
                active={sortBy === "doa"}
                direction={sortDir}
                onClick={() => setSortBy("doa")}
              >
                DOA
              </TableSortLabel>
            </TableCell>

            {canEdit && <TableCell align="right">Åtgärder</TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {expandedSerials.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ fontStyle: "italic" }}>
                Inga data att visa
              </TableCell>
            </TableRow>
          )}

          {expandedSerials.map((row) => {
            const isChild = !!row.parentId;
            const hasChildren = row.children.length > 0;
            const isExpanded = isSerialsExpanded.get(row.id);

            return (
              <TableRow key={row.id}>
                <TableCell sx={{ pr: 0 }}>
                  <IconButton
                    size="small"
                    edge="start"
                    hidden={!hasChildren}
                    sx={{ my: -0.5, mx: -1 }}
                    onClick={() => handleToggleExpand(row.id)}
                  >
                    <ArrowForwardIos
                      sx={{
                        fontSize: "1em",
                        transition: "transform 0.2s",
                        ...(isExpanded && { transform: "rotate(90deg)" }),
                      }}
                    />
                  </IconButton>
                </TableCell>

                {!isChild ? (
                  <>
                    <TableCell sx={{ textWrap: "nowrap" }}>{row.item}</TableCell>
                    <TableCell sx={{ width: "100%" }}>{row.description}</TableCell>
                  </>
                ) : (
                  <TableCell colSpan={2} sx={{ width: "100%" }}>
                    <Typography component="span" sx={{ mr: 2, opacity: 0.5 }}>
                      &#x2022;
                    </Typography>
                    {row.description}
                  </TableCell>
                )}

                <TableCell sx={{ textWrap: "nowrap" }}>{row.serial}</TableCell>

                <TableCell sx={{ textWrap: "nowrap" }}>{row.room}</TableCell>

                <TableCell align="right">{row.existing ? <Check /> : null}</TableCell>

                <TableCell align="right">{row.doa ? <ErrorIcon color="error" /> : null}</TableCell>

                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {canEdit && (
                    <TableRowMenu>
                      <TableRowAction
                        icon={<EditOutlined />}
                        text="Redigera"
                        onClick={() => onAction?.(row, "edit")}
                      />

                      <TableRowAction
                        disabled={isChild || row.existing}
                        icon={<PlaylistAdd />}
                        text="Ny underprodukt"
                        onClick={() => onAction?.(row, "add-child")}
                      />

                      <TableRowAction
                        disabled={!row.editable || row.existing}
                        icon={<DeleteOutline />}
                        text="Ta bort"
                        onClick={() => onAction?.(row, "delete")}
                      />
                    </TableRowMenu>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default OrderSerialsTable;
